import clsx from "clsx";
import { IconContainer, IconSize } from "../IconContainer";
import {
  ButtonProps,
  ButtonSize,
  ButtonSizes,
  ButtonType,
  ButtonTypes,
} from "./types";
import { Spinner } from "../Spinner";

/**
 NOTE: these buttons are based on
 @see https://www.figma.com/design/0t4c7i8lLjFFDOvE04a0IP/Experience%3A-EA-Partner-Journey?node-id=1267-14368&t=wcVwfBHka2h0dyAU-4
 */

const MAP_TYPE_TO_CLASS = {
  [ButtonType.primary]: clsx(
    "text-gray-950",
    // Contextual theme colors
    "theme:bg-primary-500 hover:theme:bg-primary-700",
    // Default colors
    "bg-green-500 hover:bg-green-700",
    // Disabled styles
    "disabled:bg-gray-200 disabled:text-gray-425 disabled:pointer-events-none dark:disabled:border-transparent dark:disabled:bg-gray-800 dark:disabled:text-gray-350",
  ),
  [ButtonType.secondary]: clsx(
    "border",
    // Contextual theme colors
    "theme:text-primary-500 theme:border-primary-500 hover:theme:border-primary-700 hover:theme:text-primary-700",
    // Default colors
    "text-green-500 border-green-500 hover:border-green-700 hover:text-green-700",
  ),
  [ButtonType.inverse]: clsx("text-gray-950 bg-white hover:bg-gray-300"),
};

export function getClassNameByType(buttonType: ButtonTypes) {
  return MAP_TYPE_TO_CLASS[buttonType];
}

const MAP_SIZE_TO_CLASS = {
  [ButtonSize.sm]: "h-9 px-3 text-sm gap-1",
  [ButtonSize.default]: "h-11 px-5 text-base gap-1",
};

export function getClassNameBySize(size?: ButtonSizes) {
  return MAP_SIZE_TO_CLASS[size ?? ButtonSize.default];
}

const MAP_MD_SIZE_TO_CLASS = {
  [ButtonSize.sm]: "md:h-9 md:px-3 md:text-sm md:gap-1",
  [ButtonSize.default]: "md:h-11 md:px-5 md:text-base md:gap-1",
};

export function getClassNameByMdSize(size?: ButtonSizes) {
  if (!size) return null;
  return MAP_MD_SIZE_TO_CLASS[size];
}

export function Button({
  icon,
  children,
  className,
  buttonType,
  md,
  size = ButtonSize.default,
  isLoading = false,
  ...props
}: ButtonProps) {
  const hasIcon = icon ?? isLoading;

  return (
    <button
      className={clsx(
        className,
        // Shared styles
        "font-roobert font-medium",
        "rounded-md inline-flex justify-center items-center",
        "transition-all duration-300 ease-in-out",
        // Configurable styles
        getClassNameByType(buttonType),
        getClassNameBySize(size),
        getClassNameByMdSize(md?.size),
      )}
      {...props}
    >
      {children && (
        <span className="inline-flex whitespace-nowrap">{children}</span>
      )}
      {hasIcon && (
        <IconContainer
          size={IconSize.sm}
          display="flex"
          className="items-center justify-center [&>svg]:w-full"
        >
          {isLoading ? <Spinner /> : icon}
        </IconContainer>
      )}
    </button>
  );
}
